import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { apiService } from '../../../services/api';
import './ActivityLog.css';
import Pagination from '../shared/Pagination';
import Timestamp from '../shared/Timestamp';
import { formatDateRange, toIsoInstant } from '../../../shared/datetime/timestamps';

// The organization allowance runs for a calendar month in UTC, sent as [start, end)
// instants. Its days are read in UTC, not the display zone, or a western reader would see
// the month start on the 30th; the last day is the one before `end`.
function utcMonthLabel(start, end) {
  const first = toIsoInstant(start);
  const after = toIsoInstant(end);
  if (!first || !after) return null;
  const last = toIsoInstant(new Date(Date.parse(after) - 1));
  return formatDateRange(first.slice(0, 10), last.slice(0, 10));
}

const PAGE_SIZE = 15;

// Pre-fetch render state only — real figures replace these as soon as the usage call
// returns. Segments share the category-chip palette so the chart and the pills read
// as the same colour system (PRD-1076 #32): the ResUI --color-surface-chip-* tokens.
const CATEGORY_CHART_COLORS = {
  Query: 'var(--color-surface-chip-query)',
  Datasets: 'var(--color-surface-chip-data)',
  Automation: 'var(--color-surface-chip-automation)',
  System: 'var(--color-surface-chip-system)',
};
const PREVIEW_TOKENS = {
  preview: true,
  used: 1250000,
  quota: 2000000,
  segments: [
    { key: 'Query', label: 'Query', tokens: 700000, color: CATEGORY_CHART_COLORS.Query },
    { key: 'Datasets', label: 'Datasets', tokens: 250000, color: CATEGORY_CHART_COLORS.Datasets },
    { key: 'Automation', label: 'Automation', tokens: 250000, color: CATEGORY_CHART_COLORS.Automation },
    { key: 'System', label: 'System', tokens: 50000, color: CATEGORY_CHART_COLORS.System },
  ],
};

// Fallback only, used until the live category list arrives. The list is fetched from
// the API because a hardcoded copy drifts from the data: this one offered "Datasets"
// (which no row uses) and omitted "Data" (which many do), so those rows were
// unreachable by any filter.
const CATEGORY_FALLBACK = ['All categories'];

// Up to two decimals with trailing zeros dropped, so a 2B quota and 1,998,750,000
// remaining both read as 2B rather than one of them as 2.00B.
const fmtScaled = (n, size, unit) => `${parseFloat((n / size).toFixed(2))}${unit}`;

// Thresholds sit where the smaller unit would round up to 1000, so 999,999 reads
// as 1M (not 1000K) and a 2B quota reads as 2B (not 2000M).
const fmtCompact = (n) => {
  if (typeof n !== 'number') return '—';
  if (n >= 999995000) return fmtScaled(n, 1000000000, 'B');
  if (n >= 999500) return fmtScaled(n, 1000000, 'M');
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return String(n);
};

// Not everything the platform bills for is tokens — speech is charged per audio-second
// and OCR per page. Those amounts must not be rendered through fmtCompact under a
// "Tokens" heading, which would read 3 pages as 3 tokens.
const fmtUnit = (unit, quantity) => {
  if (typeof quantity !== 'number') return null;
  if (unit === 'pages') return `${Math.round(quantity)} ${Math.round(quantity) === 1 ? 'page' : 'pages'}`;
  if (unit === 'audio_seconds') {
    if (quantity >= 3600) return `${(quantity / 3600).toFixed(1)} hr`;
    if (quantity >= 60) return `${Math.round(quantity / 60)} min`;
    return `${Math.round(quantity)} sec`;
  }
  return null;
};

const statusClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('fail') || s.includes('error') || s.includes('declin')) return 'activity-status--fail';
  return 'activity-status--ok';
};

// Category -> Figma activity-type chip variant (PRD-1361), matching the Audit
// page. Settings and unknown categories have no Figma variant: Standard.
const categoryVariant = (category) => {
  const c = (category || '').toLowerCase();
  if (c.includes('auth')) return 'authentication';
  // "datasets" collapses into the data chip so the pill matches the chart
  // (both use chip-data) — PRD-1076 #32.
  if (c.includes('data')) return 'data';
  if (c.includes('quer')) return 'queries';
  if (c.includes('automat')) return 'automations';
  if (c.includes('system')) return 'system';
  return 'standard';
};

/**
 * Activity Log (PRD-09). The CURRENT user's own history — distinct from Audit Logs
 * (org-wide, all users). Reuses the audit-logs API scoped to the caller (mine=true).
 * Token Usage is a placeholder until the Phase B metering backend exists.
 */
function ActivityLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState('All categories');
  const [days, setDays] = useState(30); // date-range filter: Last 7 / 30 / 90 days
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const [tokens, setTokens] = useState(PREVIEW_TOKENS);
  const [categoryOptions, setCategoryOptions] = useState(CATEGORY_FALLBACK);
  const [orgUsage, setOrgUsage] = useState(null);
  // Non-token spend for the scope on screen. Kept separate from orgUsage because
  // /usage/me reports the caller's OWN pages and audio-seconds too — gating this on
  // orgUsage meant your own non-token spend never appeared on the "You" tab.
  const [unitTotals, setUnitTotals] = useState([]);
  const [scope, setScope] = useState('me'); // 'me' = your usage, 'org' = whole organisation

  // Load usage for the selected scope and date range.
  // The range matters: the card used to fetch once on mount with no period at all while
  // the table filtered by `days`, so the two answered over different spans and their
  // totals could never be reconciled on screen. Passing the same `days` to both fixes it.
  useEffect(() => {
    let alive = true;
    const fetcher = scope === 'org' ? apiService.getOrgUsage : apiService.getMyUsage;
    fetcher
      .call(apiService, { days })
      .then((u) => {
        if (!alive || !u) return;
        const b = u.breakdown || {};
        setOrgUsage(scope === 'org' ? u : null);
        setUnitTotals(Array.isArray(u.unit_totals) ? u.unit_totals : []);
        setTokens({
          preview: false,
          used: u.used_tokens || 0,
          // quota_tokens is null when no allowance is configured — keep null so
          // the "of X / remaining" framing is hidden rather than faked.
          quota: typeof u.quota_tokens === 'number' ? u.quota_tokens : null,
          // The quota is a monthly, org-wide allowance, so `remaining` is measured
          // against org month-to-date — deliberately NOT against `used`, which follows
          // the selected date range and covers only this user on the "You" tab. The two
          // are rendered as separate statements below; subtracting one from the other
          // is what produced "1.99M remaining" for an org already over its limit.
          remaining: typeof u.remaining_tokens === 'number' ? u.remaining_tokens : null,
          monthToDate: typeof u.org_month_to_date_tokens === 'number' ? u.org_month_to_date_tokens : null,
          // The figures follow the selected window, which the API sends as instants.
          usedPeriodLabel: `Last ${days} days`,
          quotaPeriodLabel: utcMonthLabel(u.quota_period_start, u.quota_period_end),
          segments: [
            { key: 'Query', label: 'Query', tokens: b.query || 0, color: CATEGORY_CHART_COLORS.Query },
            { key: 'Datasets', label: 'Datasets', tokens: b.datasets || 0, color: CATEGORY_CHART_COLORS.Datasets },
            { key: 'Automation', label: 'Automation', tokens: b.automation || 0, color: CATEGORY_CHART_COLORS.Automation },
            { key: 'System', label: 'System', tokens: b.system || 0, color: CATEGORY_CHART_COLORS.System },
          ],
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [days, scope]);

  // Filter options come from the data, so an option can never point at zero rows and a
  // populated category can never be missing from the list.
  useEffect(() => {
    let alive = true;
    apiService
      .getAuditLogCategories()
      .then((cats) => {
        if (!alive || !Array.isArray(cats) || cats.length === 0) return;
        setCategoryOptions(['All categories', ...cats]);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const t = tokens;
  const hasQuota = typeof t.quota === 'number' && t.quota > 0;
  // Preview computes quota − used; real data uses the backend's remaining_tokens.
  const remaining =
    typeof t.remaining === 'number' ? t.remaining : t.preview && hasQuota ? Math.max(0, t.quota - t.used) : null;
  const remainingIsNumber = typeof remaining === 'number';
  const remainingLabel = remainingIsNumber ? fmtCompact(remaining) : null;
  // The allowance line is a separate sentence about the org's month, shown only when
  // there is a real quota to report. It is NOT "used vs quota": `used` is the selected
  // window (and only this user on the You tab), so the two never belonged in one
  // subtraction — that is what let an over-quota org read as having headroom.
  const monthToDateIsNumber = typeof t.monthToDate === 'number';
  const showAllowance = hasQuota && monthToDateIsNumber;
  const overQuota = showAllowance && t.monthToDate > t.quota;
  // Fill the bar against the window's own total so the buckets stay proportional to what
  // the headline number actually describes.
  const barDenom = t.preview && hasQuota ? t.quota : t.used || 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getAuditLogs({
        mine: true,
        page,
        page_size: PAGE_SIZE,
        category: category === 'All categories' ? undefined : category,
        q: search.trim() || undefined,
        days,
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotalPages(res?.total_pages || 1);
    } catch (e) {
      setError(e?.message || 'Failed to load activity');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, category, search, days]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to page 1 when a filter changes.
  useEffect(() => {
    setPage(1);
  }, [category, search, days]);

  return (
    <div className="activity-log-page">
      <div className="activity-card activity-token-card">
        <div className="activity-token-head">
          {/* The scope is named explicitly: "your" usage is compared against the ORG's
              quota, so without a label two users at 90% each would both read as healthy. */}
          <div className="activity-token-title">{scope === 'org' ? 'Organization Usage' : 'Your Token Usage'}</div>
          {t.preview && <Chip className="activity-token-preview">Preview</Chip>}
          <div className="activity-token-scope">
            <button
              type="button"
              className={`activity-scope-btn${scope === 'me' ? ' is-active' : ''}`}
              onClick={() => setScope('me')}
            >
              You
            </button>
            <button
              type="button"
              className={`activity-scope-btn${scope === 'org' ? ' is-active' : ''}`}
              onClick={() => setScope('org')}
            >
              Organization
            </button>
          </div>
        </div>
        <div className="activity-token-summary">
          <strong>{fmtCompact(t.used)}</strong> tokens used
          {t.usedPeriodLabel ? <> · {t.usedPeriodLabel.toLowerCase()}</> : null}
        </div>
        {showAllowance && (
          <div className={`activity-token-allowance${overQuota ? ' is-over' : ''}`}>
            Organization allowance{t.quotaPeriodLabel ? ` (${t.quotaPeriodLabel})` : ''}:{' '}
            <strong>{fmtCompact(t.monthToDate)}</strong> of <strong>{fmtCompact(t.quota)}</strong> used
            {overQuota ? (
              <> · <strong>over allowance</strong></>
            ) : (
              remainingLabel && (
                <>
                  {' '}· <strong>{remainingLabel}</strong> remaining
                </>
              )
            )}
          </div>
        )}
        <div className="activity-token-bar">
          {t.segments.map((s) => (
            <div
              key={s.key}
              className="activity-token-seg"
              style={{ width: `${(s.tokens / barDenom) * 100}%`, background: s.color }}
            />
          ))}
        </div>
        <div className="activity-token-legend">
          {t.segments.map((s) => (
            <span key={s.key} className="activity-token-legend-item">
              <span className="activity-token-dot" style={{ background: s.color }} />
              {s.label} <strong>{fmtCompact(s.tokens)}</strong>
            </span>
          ))}
          {remainingLabel && (
            <span className="activity-token-legend-item">
              <span className="activity-token-dot activity-token-dot--remaining" />
              Remaining <strong>{remainingLabel}</strong>
            </span>
          )}
        </div>
        {/* Non-token spend is shown alongside the token total rather than folded into
            it, because pages and audio-seconds don't add up with tokens. */}
        {unitTotals.length > 0 && (
          <div className="activity-token-units">
            {unitTotals.map((u) => (
              <span key={u.unit} className="activity-token-legend-item">
                {fmtUnit(u.unit, u.quantity) || `${u.quantity} ${u.unit}`}
                <span className="activity-token-units-sub"> across {u.events} runs</span>
              </span>
            ))}
          </div>
        )}
        {scope === 'org' && orgUsage?.unattributed_tokens > 0 && (
          // The org is billed for this either way; it simply has no owning user, so it
          // can never appear on anyone's personal view. Saying so beats hiding it.
          <div className="activity-token-units">
            <span className="activity-token-legend-item">
              <strong>{fmtCompact(orgUsage.unattributed_tokens)}</strong>
              <span className="activity-token-units-sub"> not attributable to a specific user</span>
            </span>
          </div>
        )}
      </div>

      {/* Filters + table */}
      <div className="activity-card">
        <div className="activity-toolbar">
          <input
            className="activity-search"
            type="search"
            placeholder="Search activity"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="resui-select resui-select--compact"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Date range"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <select
            className="resui-select resui-select--compact"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Category"
          >
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="activity-table-wrap">
          <table className="activity-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Category</th>
                <th>Action</th>
                <th className="activity-col-tokens">Usage</th>
                <th>Status</th>
                <th aria-label="Expand" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="activity-empty">Loading…</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="activity-empty activity-error">{error}</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="activity-empty">No activity yet.</td>
                </tr>
              ) : (
                items.map((it) => (
                  <React.Fragment key={it.id}>
                    <tr className="activity-row" onClick={() => setExpanded(expanded === it.id ? null : it.id)}>
                      <td className="activity-col-time"><Timestamp value={it.time} form="static" /></td>
                      <td>
                        <Chip variant={categoryVariant(it.category)} className="activity-pill">{it.category || '—'}</Chip>
                      </td>
                      <td>{it.action || '—'}</td>
                      <td className="activity-col-tokens">
                        {/* usage_label carries non-token spend ("3 pages", "1.5 hr") already
                            formatted by the API — fmtCompact has no concept of units and
                            would render 3 pages as a bare "3" under this heading. */}
                        {it.usage_label || (it.tokens != null ? fmtCompact(it.tokens) : 'Not Applicable')}
                      </td>
                      <td>
                        <span className={`activity-status ${statusClass(it.status)}`}>{it.status || '—'}</span>
                      </td>
                      <td className="activity-col-chev">
                        <Icon name={expanded === it.id ? 'expand_less' : 'expand_more'} />
                      </td>
                    </tr>
                    {expanded === it.id && (
                      <tr className="activity-detail-row">
                        <td colSpan={6}>
                          <div className="activity-detail">
                            {it.query && (
                              <>
                                <span className="activity-detail-k">Query</span>
                                <span className="activity-detail-v">{it.query}</span>
                              </>
                            )}
                            {it.response && (
                              <>
                                <span className="activity-detail-k">ResAI response</span>
                                <span className="activity-detail-v">{it.response}</span>
                              </>
                            )}
                            {it.error && (
                              <>
                                <span className="activity-detail-k">Why it failed</span>
                                <span className="activity-detail-v activity-detail-error">{it.error}</span>
                              </>
                            )}
                            {Array.isArray(it.attachments) && it.attachments.length > 0 && (
                              <>
                                <span className="activity-detail-k">Attachments</span>
                                <span className="activity-detail-v activity-attach-list">
                                  {it.attachments.map((a, i) => (
                                    <Chip key={i} className="activity-attach-chip">
                                      {a?.name || 'file'}
                                      {a?.size ? ` · ${a.size}` : ''}
                                    </Chip>
                                  ))}
                                </span>
                              </>
                            )}
                            {/* Ask ResAI rows: dataset(s) live in `details` ("Dataset: …"). */}
                            {(it.query || it.response || it.error) && it.details && (
                              <>
                                <span className="activity-detail-k">Dataset</span>
                                <span className="activity-detail-v">{it.details.replace(/^Dataset:\s*/i, '')}</span>
                              </>
                            )}
                            {/* Non-query rows: a single multi-line Details block. */}
                            {!it.query && !it.response && !it.error && (
                              it.details ? (
                                <>
                                  <span className="activity-detail-k">Details</span>
                                  <span className="activity-detail-v">{it.details}</span>
                                </>
                              ) : (
                                <span className="activity-detail-v">No additional detail.</span>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
}

export default ActivityLog;
